import { useEffect, useLayoutEffect, useRef } from 'react';
import { NavigationType, useLocation, useNavigationType } from 'react-router';

/**
 * Scrollposition je Seite (Verlaufseintrag): Beim Zurück-Navigieren (Browser-Zurück, Zurück-Links) wird die
 * gemerkte Position wiederhergestellt, neue Seiten beginnen oben. `ScrollRestoration` von React Router gibt es
 * nur mit Daten-Routern (hier `BrowserRouter`), daher ein eigener Hook mit `sessionStorage` je `location.key`.
 * Speicher darf fehlen (Privatmodus): dann bleibt es beim Browserverhalten ohne Wiederherstellung.
 */
const PREFIX = 'barso:scroll:';
/** So lange wird auf nachladende Inhalte gewartet, bevor die Position aufgegeben wird. */
const RESTORE_WINDOW_MS = 2_000;

function readPosition(key: string): number | null {
  try {
    const raw = window.sessionStorage.getItem(`${PREFIX}${key}`);
    if (raw === null) return null;
    const value = Number(raw);
    return Number.isFinite(value) && value >= 0 ? value : null;
  } catch {
    return null;
  }
}

/** Höchstzahl gemerkter Verlaufseinträge: ältere werden verworfen, damit der Speicher nicht wächst. */
export const MAX_ENTRIES = 100;
const INDEX_KEY = `${PREFIX}index`;

/** Merkt die Reihenfolge der Schlüssel und entfernt die ältesten über `MAX_ENTRIES` hinaus. */
function track(storage: Storage, key: string): void {
  let keys: string[] = [];
  try {
    const parsed: unknown = JSON.parse(storage.getItem(INDEX_KEY) ?? '[]');
    if (Array.isArray(parsed))
      keys = parsed.filter((entry): entry is string => typeof entry === 'string');
  } catch {
    keys = [];
  }
  if (keys[keys.length - 1] === key) return;
  keys = keys.filter((entry) => entry !== key);
  keys.push(key);
  for (const old of keys.splice(0, Math.max(0, keys.length - MAX_ENTRIES))) {
    storage.removeItem(`${PREFIX}${old}`);
  }
  storage.setItem(INDEX_KEY, JSON.stringify(keys));
}

/**
 * Schlüssel eines Verlaufseintrags: Der erste Eintrag einer Sitzung heißt immer `default`, auch nach einem Neuladen
 * auf einer anderen Seite. Mit dem Pfad dazu bekommt jede Seite ihre eigene Position.
 */
export const positionKey = (location: { key: string; pathname: string }): string =>
  `${location.key}:${location.pathname}`;

function writePosition(key: string, value: number): void {
  try {
    window.sessionStorage.setItem(`${PREFIX}${key}`, String(Math.round(value)));
    track(window.sessionStorage, key);
  } catch {
    // Speicher voll oder gesperrt: Position geht verloren, sonst nichts
  }
}

export function useScrollRestoration(): void {
  const location = useLocation();
  const navigationType = useNavigationType();
  const keyRef = useRef(positionKey(location));
  const previousPath = useRef(location.pathname);

  // Browser soll nicht selbst wiederherstellen (kommt sonst zu früh, wenn der Inhalt noch lädt)
  useEffect(() => {
    const previous = window.history.scrollRestoration;
    try {
      window.history.scrollRestoration = 'manual';
    } catch {
      // nicht unterstützt
    }
    return () => {
      try {
        window.history.scrollRestoration = previous;
      } catch {
        // nicht unterstützt
      }
    };
  }, []);

  // Position des aktuellen Eintrags laufend merken (gedrosselt auf einen Frame)
  useEffect(() => {
    let frame = 0;
    const remember = () => {
      frame = 0;
      writePosition(keyRef.current, window.scrollY);
    };
    const onScroll = () => {
      if (frame === 0) frame = window.requestAnimationFrame(remember);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame !== 0) window.cancelAnimationFrame(frame);
    };
  }, []);

  useLayoutEffect(() => {
    keyRef.current = positionKey(location);
    const pathChanged = previousPath.current !== location.pathname;
    previousPath.current = location.pathname;
    if (navigationType === NavigationType.Pop) {
      const target = readPosition(positionKey(location));
      if (target === null || target === 0) {
        if (window.scrollY !== 0) window.scrollTo(0, 0);
        return;
      }
      // Inhalt lädt oft nach: so lange versuchen, bis die Seite hoch genug ist
      const started = Date.now();
      let frame = 0;
      let stopped = false;
      const stop = () => {
        stopped = true;
        window.cancelAnimationFrame(frame);
      };
      const attempt = () => {
        if (stopped) return;
        const reachable = document.documentElement.scrollHeight - window.innerHeight;
        if (reachable >= target || Date.now() - started > RESTORE_WINDOW_MS) {
          window.scrollTo(0, Math.min(target, Math.max(reachable, 0)));
          return;
        }
        frame = window.requestAnimationFrame(attempt);
      };
      attempt();
      // Greift die Person selbst ein, hat das Vorrang
      window.addEventListener('wheel', stop, { once: true, passive: true });
      window.addEventListener('touchstart', stop, { once: true, passive: true });
      return () => {
        stop();
        window.removeEventListener('wheel', stop);
        window.removeEventListener('touchstart', stop);
      };
    }
    // Neue Seite: oben beginnen (Sprungmarke und reine Such-/Filteränderung der Seite bleiben unberührt)
    if (location.hash === '' && pathChanged) {
      if (window.scrollY !== 0) window.scrollTo(0, 0);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `location` ist über Schlüssel, Pfad und Anker abgedeckt
  }, [location.key, location.pathname, location.hash, navigationType]);
}
